/**
 * Campaigns Page
 * 
 * Manages outbound call campaigns:
 * - Import leads from SFTP
 * - View campaign list with status
 * - Launch batch calls with retry logic
 * - Monitor call progress in real-time
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Megaphone, Download, Play, Pause, Phone, Users, CheckCircle, 
  XCircle, Clock, AlertTriangle, RefreshCw, ChevronRight, 
  FileUp, Loader2, PhoneCall, PhoneOff, PhoneMissed
} from "lucide-react";

const statusColors: Record<string, string> = {
  draft: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  paused: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
};

const leadStatusColors: Record<string, string> = {
  new: "bg-gray-500/20 text-gray-400",
  calling: "bg-blue-500/20 text-blue-400",
  contacted: "bg-emerald-500/20 text-emerald-400",
  no_answer: "bg-amber-500/20 text-amber-400",
  voicemail: "bg-purple-500/20 text-purple-400",
  callback: "bg-cyan-500/20 text-cyan-400",
  completed: "bg-green-500/20 text-green-400",
  dnc: "bg-red-500/20 text-red-400",
  failed: "bg-red-500/20 text-red-400",
};

const leadStatusIcons: Record<string, React.ReactNode> = {
  new: <Clock className="w-3 h-3" />,
  calling: <PhoneCall className="w-3 h-3" />,
  contacted: <CheckCircle className="w-3 h-3" />,
  no_answer: <PhoneMissed className="w-3 h-3" />,
  voicemail: <PhoneOff className="w-3 h-3" />,
  completed: <CheckCircle className="w-3 h-3" />,
  dnc: <XCircle className="w-3 h-3" />,
  failed: <AlertTriangle className="w-3 h-3" />,
};

export default function Campaigns() {
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsFilter, setLeadsFilter] = useState<string | undefined>(undefined);

  // Queries
  const campaignsQuery = trpc.campaigns.list.useQuery();
  const fileImportsQuery = trpc.fileImport.list.useQuery();
  
  const campaignDetailQuery = trpc.campaigns.get.useQuery(
    { id: selectedCampaignId! },
    { enabled: !!selectedCampaignId }
  );

  const leadsQuery = trpc.campaigns.getLeads.useQuery(
    { campaignId: selectedCampaignId!, status: leadsFilter, page: leadsPage, pageSize: 50 },
    { enabled: !!selectedCampaignId }
  );

  const callableCountQuery = trpc.campaigns.getCallableCount.useQuery(
    { campaignId: selectedCampaignId! },
    { enabled: !!selectedCampaignId && campaignDetailQuery.data?.status === 'active' }
  );

  // Mutations
  const importFromSftp = trpc.fileImport.importFromSftp.useMutation({
    onSuccess: (data) => {
      toast.success(`Imported ${data.filesImported} files from SFTP`);
      campaignsQuery.refetch();
      fileImportsQuery.refetch();
    },
    onError: (err) => toast.error(`Import failed: ${err.message}`),
  });

  const activateCampaign = trpc.campaigns.activate.useMutation({
    onSuccess: () => {
      toast.success("Campaign activated");
      campaignsQuery.refetch();
      campaignDetailQuery.refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const pauseCampaign = trpc.campaigns.pause.useMutation({
    onSuccess: () => {
      toast.success("Campaign paused");
      campaignsQuery.refetch();
      campaignDetailQuery.refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const runBatch = trpc.campaigns.runBatch.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.message}`);
      leadsQuery.refetch();
      campaignDetailQuery.refetch();
      callableCountQuery.refetch();
    },
    onError: (err) => toast.error(`Batch failed: ${err.message}`),
  });

  const callSingleLead = trpc.campaigns.callLead.useMutation({
    onSuccess: (data) => {
      toast.success(`Call initiated: ${data.callId}`);
      leadsQuery.refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  // Campaign list view
  if (!selectedCampaignId) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Call Campaigns</h1>
            <p className="text-sm text-gray-400 mt-1">Manage outbound call campaigns with automated retry logic</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => importFromSftp.mutate()}
              disabled={importFromSftp.isPending}
              className="border-gray-600 hover:bg-gray-800"
            >
              {importFromSftp.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Import from SFTP
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Megaphone className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{campaignsQuery.data?.length || 0}</p>
                  <p className="text-xs text-gray-400">Total Campaigns</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Play className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {campaignsQuery.data?.filter(c => c.status === 'active').length || 0}
                  </p>
                  <p className="text-xs text-gray-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Users className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {campaignsQuery.data?.reduce((sum, c) => sum + (c.totalLeads || 0), 0) || 0}
                  </p>
                  <p className="text-xs text-gray-400">Total Leads</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <FileUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{fileImportsQuery.data?.length || 0}</p>
                  <p className="text-xs text-gray-400">Files Imported</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Campaigns</CardTitle>
            <CardDescription>Click a campaign to view details and manage calls</CardDescription>
          </CardHeader>
          <CardContent>
            {campaignsQuery.isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : !campaignsQuery.data?.length ? (
              <div className="text-center py-12">
                <Megaphone className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">No campaigns yet</p>
                <p className="text-sm text-gray-500">Import files from SFTP to create campaigns automatically</p>
                <Button
                  variant="outline"
                  className="mt-4 border-gray-600"
                  onClick={() => importFromSftp.mutate()}
                  disabled={importFromSftp.isPending}
                >
                  {importFromSftp.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Import from SFTP
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">Campaign</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Leads</TableHead>
                    <TableHead className="text-gray-400">Contacted</TableHead>
                    <TableHead className="text-gray-400">Completed</TableHead>
                    <TableHead className="text-gray-400">Created</TableHead>
                    <TableHead className="text-gray-400"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignsQuery.data.map((campaign) => (
                    <TableRow
                      key={campaign.id}
                      className="border-gray-800 cursor-pointer hover:bg-gray-800/50 transition-colors"
                      onClick={() => setSelectedCampaignId(campaign.id)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{campaign.name}</p>
                          <p className="text-xs text-gray-500">{campaign.clientName}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[campaign.status]} border text-xs`}>
                          {campaign.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300">{campaign.totalLeads}</TableCell>
                      <TableCell className="text-gray-300">{campaign.leadsContacted || 0}</TableCell>
                      <TableCell className="text-gray-300">{campaign.leadsCompleted || 0}</TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {new Date(campaign.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Campaign detail view
  const campaign = campaignDetailQuery.data;
  const leads = leadsQuery.data;

  return (
    <div className="space-y-6">
      {/* Back button + Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { setSelectedCampaignId(null); setLeadsFilter(undefined); setLeadsPage(1); }}
          className="text-gray-400 hover:text-white"
        >
          ← Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{campaign?.name || 'Loading...'}</h1>
          <p className="text-sm text-gray-400 mt-1">{campaign?.description}</p>
        </div>
        <div className="flex gap-2">
          {campaign?.status === 'draft' && (
            <Button
              onClick={() => activateCampaign.mutate({ id: selectedCampaignId! })}
              disabled={activateCampaign.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Play className="w-4 h-4 mr-2" />
              Activate
            </Button>
          )}
          {campaign?.status === 'active' && (
            <>
              <Button
                onClick={() => runBatch.mutate({ campaignId: selectedCampaignId! })}
                disabled={runBatch.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {runBatch.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Phone className="w-4 h-4 mr-2" />
                )}
                {runBatch.isPending ? 'Calling...' : `Launch Calls${callableCountQuery.data ? ` (${callableCountQuery.data.count})` : ''}`}
              </Button>
              <Button
                variant="outline"
                onClick={() => pauseCampaign.mutate({ id: selectedCampaignId! })}
                disabled={pauseCampaign.isPending}
                className="border-amber-600 text-amber-400 hover:bg-amber-600/10"
              >
                <Pause className="w-4 h-4 mr-2" />
                Pause
              </Button>
            </>
          )}
          {campaign?.status === 'paused' && (
            <Button
              onClick={() => activateCampaign.mutate({ id: selectedCampaignId! })}
              disabled={activateCampaign.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Play className="w-4 h-4 mr-2" />
              Resume
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => { leadsQuery.refetch(); campaignDetailQuery.refetch(); callableCountQuery.refetch(); }}
            className="border-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Campaign Stats */}
      {campaign && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {(campaign.statusBreakdown || []).map((s: { status: string; count: number }) => (
            <Card key={s.status} className="bg-gray-900/50 border-gray-800">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-1">
                  {leadStatusIcons[s.status] || <Clock className="w-3 h-3" />}
                  <span className="text-xs text-gray-400 capitalize">{s.status.replace('_', ' ')}</span>
                </div>
                <p className="text-xl font-bold text-white">{s.count}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Campaign Config */}
      {campaign && (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <div>
                <p className="text-gray-500">Status</p>
                <Badge className={`${statusColors[campaign.status]} border mt-1`}>{campaign.status}</Badge>
              </div>
              <div>
                <p className="text-gray-500">Max Attempts/Day</p>
                <p className="text-white font-medium mt-1">{campaign.maxAttemptsPerDay}</p>
              </div>
              <div>
                <p className="text-gray-500">Min Time Between</p>
                <p className="text-white font-medium mt-1">{campaign.minMinutesBetweenAttempts} min</p>
              </div>
              <div>
                <p className="text-gray-500">Call Window (EST)</p>
                <p className="text-white font-medium mt-1">{campaign.callWindowStart} - {campaign.callWindowEnd}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={!leadsFilter ? "default" : "outline"}
          size="sm"
          onClick={() => { setLeadsFilter(undefined); setLeadsPage(1); }}
          className={!leadsFilter ? "bg-gray-700" : "border-gray-700 text-gray-400"}
        >
          All ({leads?.total || 0})
        </Button>
        {['new', 'calling', 'contacted', 'no_answer', 'completed', 'dnc', 'failed'].map(status => (
          <Button
            key={status}
            variant={leadsFilter === status ? "default" : "outline"}
            size="sm"
            onClick={() => { setLeadsFilter(status); setLeadsPage(1); }}
            className={leadsFilter === status ? "bg-gray-700" : "border-gray-700 text-gray-400"}
          >
            {status.replace('_', ' ')}
          </Button>
        ))}
      </div>

      {/* Leads Table */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="pt-6">
          {leadsQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">Name</TableHead>
                    <TableHead className="text-gray-400">Phone</TableHead>
                    <TableHead className="text-gray-400">Dealer</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Attempts</TableHead>
                    <TableHead className="text-gray-400">Last Attempt</TableHead>
                    <TableHead className="text-gray-400">Outcome</TableHead>
                    <TableHead className="text-gray-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads?.leads.map((lead) => (
                    <TableRow key={lead.id} className="border-gray-800 hover:bg-gray-800/30">
                      <TableCell>
                        <p className="font-medium text-white">{lead.firstName} {lead.lastName}</p>
                        <p className="text-xs text-gray-500">Loan: {lead.loanNumber}</p>
                      </TableCell>
                      <TableCell className="text-gray-300 font-mono text-sm">{lead.phoneNumber}</TableCell>
                      <TableCell className="text-gray-300 text-sm max-w-[150px] truncate">{lead.dealer}</TableCell>
                      <TableCell>
                        <Badge className={`${leadStatusColors[lead.status]} text-xs flex items-center gap-1 w-fit`}>
                          {leadStatusIcons[lead.status]}
                          {lead.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {lead.attemptsToday}/{campaign?.maxAttemptsPerDay || 3} today
                        <span className="text-gray-500 text-xs ml-1">({lead.totalAttempts} total)</span>
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {lead.lastAttemptAt ? new Date(lead.lastAttemptAt).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-gray-300 text-sm max-w-[120px] truncate">
                        {lead.callOutcome || '—'}
                      </TableCell>
                      <TableCell>
                        {campaign?.status === 'active' && ['new', 'no_answer'].includes(lead.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              callSingleLead.mutate({ campaignId: selectedCampaignId!, leadId: lead.id });
                            }}
                            disabled={callSingleLead.isPending}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          >
                            <Phone className="w-3 h-3 mr-1" />
                            Call
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {leads && leads.total > 50 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-400">
                    Showing {((leadsPage - 1) * 50) + 1}-{Math.min(leadsPage * 50, leads.total)} of {leads.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={leadsPage === 1}
                      onClick={() => setLeadsPage(p => p - 1)}
                      className="border-gray-700"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={leadsPage * 50 >= leads.total}
                      onClick={() => setLeadsPage(p => p + 1)}
                      className="border-gray-700"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
